import { AfterViewInit, Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ContactUsModalComponent } from '@app/root/components/contact-us-modal/contact-us-modal.component';
import { DigitCodeComponent } from '@app/root/components/digit-code/digit-code.component';
import { LoginLayoutComponent } from '@app/root/components/login-layout/login-layout.component';
import { PassFormComponent } from '@app/root/components/pass-form/pass-form.component';
import { SharedAuthSignInChallenge } from '@libs/shared/domain/auth/sign-in.challenge.';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '@services/auth.service';
import { FeatureFlagsService } from '@services/feature-flags.service';
import { IntervalPipe } from '@shared/pipes/interval.pipe';
import Utils from '@shared/providers/utils';
import { SharedModule } from '@shared/shared.module';
import { isEmail } from 'class-validator';
import { interval, tap } from 'rxjs';

const SUCCESS: Record<string, string> = {
  reset: 'Password was changed. Now you can login.',
};

@Component({
  selector: 'app-login',
  imports: [FormsModule, SharedModule, LoginLayoutComponent, PassFormComponent, DigitCodeComponent, IntervalPipe],
  templateUrl: './login.component.html',
})
export class LoginComponent implements AfterViewInit {
  readonly authService = inject(AuthService);
  readonly featureFlagsService = inject(FeatureFlagsService);
  readonly modalService = inject(NgbModal);
  readonly route = inject(ActivatedRoute);
  readonly router = inject(Router);

  contactFormEnabled = this.featureFlagsService.select('contact-form-enabled');
  error = signal('');
  isSending = signal(false);
  success = signal(SUCCESS[this.route.snapshot.queryParamMap.get('success') || ''] || '');
  username = signal(this.route.snapshot.queryParamMap.get('name') ?? '');
  password = signal(this.route.snapshot.queryParamMap.get('pass') ?? '');
  agreedToConditions = signal(true);
  challenge = signal<null | SharedAuthSignInChallenge>(null);
  challengeResponse = signal('');
  mfaType = signal<'email' | 'sms' | null>(null);
  resend = signal(0);
  view = signal<'login' | 'confirm' | 'setupPhone' | 'mfa'>('login');

  constructor() {
    toSignal(interval(1000).pipe(tap(() => this.resend.update(s => (s > 0 ? s - 1 : 0)))));
  }

  ngAfterViewInit() {
    if (this.username() && this.password()) this.onLogin();
  }

  async onLogin() {
    if (this.isSending()) return;
    if (!this.username() || !this.password()) {
      this.error.set('Username and password are required.');
      return;
    }
    this.error.set('');
    this.isSending.set(true);
    const username = this.username().trim();
    const result = await this.authService.login(
      isEmail(username) ? username.toLowerCase() : username,
      this.password().trim(),
      this.challenge() || undefined,
      this.challengeResponse()?.trim() || undefined,
    );
    if (result.signedIn) return;
    this.isSending.set(false);
    if (result.error) {
      if (result.error.includes('TEMPORARY_PASSWORD')) {
        this.router.navigate(['/login/forgot'], { queryParams: { name: this.username() } });
      } else if (result.error.includes('MFA code') && this.view() !== 'mfa') {
        this.goToMfa(result.meta?.mfaType);
      } else {
        this.error.set(result.error);
        if (this.view() === 'mfa') this.challengeResponse.set('');
      }
    } else if (result.nextStep === 'NEW_PASSWORD_REQUIRED') {
      this.challengeResponse.set('');
      this.challenge.set(result.nextStep);
      this.view.set('confirm');
    } else if (result.nextStep === 'SETUP_PHONE') {
      this.challengeResponse.set('');
      this.challenge.set(result.nextStep);
      this.view.set('setupPhone');
    } else if (result.nextStep === 'MFA') {
      this.goToMfa(result.meta?.mfaType);
    }
  }

  goToMfa(mfaType: 'email' | 'sms' = 'sms') {
    this.challengeResponse.set('');
    this.challenge.set('MFA');
    this.mfaType.set(mfaType);
    this.view.set('mfa');
    this.resend.set(60);
  }

  reset() {
    this.challenge.set(null);
    this.challengeResponse.set('');
    this.error.set('');
    this.view.set('login');
  }

  async onPassConfirm(password: string) {
    this.challengeResponse.set(password);
    await this.onLogin();
    this.password.set(password);
  }

  async onMfa() {
    if (this.isSending()) return;
    if (!this.validateMfaCode(this.challengeResponse() || '')) {
      this.error.set('MFA code must be 6 digits long.');
      return;
    }
    await this.onLogin();
  }

  async onSetupPhone() {
    if (this.isSending()) return;
    if (!this.validatePhone(this.challengeResponse() || '')) {
      this.error.set('Phone number is not valid.');
      return;
    }
    await this.onLogin();
  }

  onResendCode() {
    if (this.isSending()) return;
    if (this.resend() > 0) return;
    this.challengeResponse.set('');
    this.challenge.set(null);
    this.onLogin();
  }

  onMfaChange(event: KeyboardEvent) {
    if (!/^[\d+]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onSetupPhoneChange(event: KeyboardEvent) {
    if (event.key === 'Enter') return;
    if (!/^[\d+]$/.test(event.key) && event.key !== '+') {
      event.preventDefault();
    }
  }

  validateMfaCode(code: string) {
    return /^\d{6}$/.test(code);
  }

  validatePhone(input: string) {
    return Utils.isPhoneNumberValid(input) || Utils.isPhoneNumberValid(input, []);
  }

  showContactForm() {
    this.modalService.open(ContactUsModalComponent, { backdrop: 'static', centered: true });
  }
}
